import { Box, Skeleton } from "@mui/material";
import React from "react";

// Skeletons específicos para diferentes tipos de conteúdo
export const ConversationSkeleton: React.FC = () => (
  <Box sx={{ p: 2, borderBottom: "1px solid #e9edef" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Skeleton variant="circular" width={40} height={40} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="60%" height={20} />
        <Skeleton variant="text" width="40%" height={16} />
      </Box>
    </Box>
  </Box>
);

export const MessageSkeleton: React.FC<{ isFromClient?: boolean }> = ({
  isFromClient = false,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: isFromClient ? "flex-start" : "flex-end",
      mb: 2,
      px: 2,
    }}
  >
    <Box
      sx={{
        maxWidth: "70%",
        backgroundColor: isFromClient ? "#ffffff" : "#d9fdd3",
        borderRadius: "7.5px",
        p: 1,
        minWidth: 120,
      }}
    >
      <Skeleton variant="text" width="80%" height={16} />
      <Skeleton variant="text" width="60%" height={12} />
    </Box>
  </Box>
);

export const TemplateSkeleton: React.FC = () => (
  <Box sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 2, mb: 2 }}>
    <Skeleton variant="text" width="70%" height={24} />
    <Skeleton variant="text" width="50%" height={16} />
    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
      <Skeleton variant="rounded" width={60} height={24} />
      <Skeleton variant="rounded" width={60} height={24} />
    </Box>
  </Box>
);

export const ContactSkeleton: React.FC = () => (
  <Box
    sx={{
      border: "1px solid #e0e0e0",
      borderRadius: 8,
      p: 2,
      mb: 1,
      backgroundColor: "#fafbfc",
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
      <Skeleton variant="rounded" width={80} height={24} />
      <Skeleton variant="rounded" width={60} height={24} />
    </Box>
    <Skeleton variant="text" width="100%" height={16} />
    <Skeleton variant="text" width="70%" height={16} />
    <Skeleton variant="text" width="50%" height={16} />
  </Box>
);

export const CampaignSkeleton: React.FC = () => (
  <Box
    sx={{
      border: "1px solid #e0e0e0",
      borderRadius: 8,
      mb: 2,
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        background: "linear-gradient(135deg, #008069 0%, #006d5b 100%)",
        padding: "12px 16px",
        minHeight: "60px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Skeleton
          variant="text"
          width="60%"
          height={20}
          sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
        />
        <Skeleton
          variant="text"
          width="40%"
          height={16}
          sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
        />
      </Box>
      <Skeleton
        variant="circular"
        width={24}
        height={24}
        sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
      />
    </Box>
    <Box sx={{ p: 1.5 }}>
      {[1, 2].map((index) => (
        <ContactSkeleton key={`contact-skeleton-${index}`} />
      ))}
    </Box>
  </Box>
);

// Componente genérico para listas
export const ListSkeleton: React.FC<{
  count?: number;
  itemHeight?: number;
  spacing?: number;
}> = ({ count = 3, itemHeight = 60, spacing = 1 }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: spacing }}>
    {Array.from({ length: count }).map((_, index) => (
      <Skeleton
        key={`list-skeleton-${index}`}
        variant="rectangular"
        height={itemHeight}
        sx={{ borderRadius: 1 }}
      />
    ))}
  </Box>
);

// Componente para grids
export const GridSkeleton: React.FC<{
  rows?: number;
  cols?: number;
  itemHeight?: number;
}> = ({ rows = 3, cols = 2, itemHeight = 120 }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: `repeat(${cols}, 1fr)`,
      gap: 2,
    }}
  >
    {Array.from({ length: rows * cols }).map((_, index) => (
      <Skeleton
        key={`grid-skeleton-${index}`}
        variant="rectangular"
        height={itemHeight}
        sx={{ borderRadius: 1 }}
      />
    ))}
  </Box>
);

// Componente para cards
export const CardSkeleton: React.FC<{
  showAvatar?: boolean;
  lines?: number;
}> = ({ showAvatar = false, lines = 2 }) => (
  <Box sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}>
    {showAvatar && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Skeleton variant="circular" width={40} height={40} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="text" width="40%" height={16} />
        </Box>
      </Box>
    )}
    {Array.from({ length: lines }).map((_, index) => (
      <Skeleton
        key={`card-skeleton-line-${index}`}
        variant="text"
        width={`${80 - index * 10}%`}
        height={16}
        sx={{ mb: 1 }}
      />
    ))}
  </Box>
);

// Skeleton para tabelas
export const TableSkeleton: React.FC<{
  rows?: number;
  cols?: number;
  rowHeight?: number;
}> = ({ rows = 5, cols = 4, rowHeight = 56 }) => (
  <Box sx={{ width: "100%" }}>
    {/* Header da tabela */}
    <Box sx={{ display: "flex", borderBottom: "1px solid #e0e0e0", py: 1 }}>
      {Array.from({ length: cols }).map((_, index) => (
        <Box key={`table-header-${index}`} sx={{ flex: 1, px: 2 }}>
          <Skeleton variant="text" width="80%" height={20} />
        </Box>
      ))}
    </Box>
    {/* Linhas da tabela */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <Box
        key={`table-row-${rowIndex}`}
        sx={{ display: "flex", py: 1, borderBottom: "1px solid #f0f0f0" }}
      >
        {Array.from({ length: cols }).map((_, colIndex) => (
          <Box
            key={`table-cell-${rowIndex}-${colIndex}`}
            sx={{ flex: 1, px: 2 }}
          >
            <Skeleton
              variant="text"
              width={`${60 + (colIndex % 3) * 10}%`}
              height={16}
            />
          </Box>
        ))}
      </Box>
    ))}
  </Box>
);

// Skeleton para formulários
export const FormSkeleton: React.FC<{
  fields?: number;
  showButtons?: boolean;
}> = ({ fields = 4, showButtons = true }) => (
  <Box sx={{ p: 2 }}>
    {Array.from({ length: fields }).map((_, index) => (
      <Box key={`form-field-${index}`} sx={{ mb: 2 }}>
        <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" width="100%" height={40} />
      </Box>
    ))}
    {showButtons && (
      <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
        <Skeleton variant="rectangular" width={100} height={36} />
        <Skeleton variant="rectangular" width={100} height={36} />
      </Box>
    )}
  </Box>
);

// Skeleton para estatísticas/cards de métricas
export const StatsSkeleton: React.FC<{
  items?: number;
}> = ({ items = 4 }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: 2,
      p: 2,
    }}
  >
    {Array.from({ length: items }).map((_, index) => (
      <Box
        key={`stats-item-${index}`}
        sx={{
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          backgroundColor: "#fafbfc",
        }}
      >
        <Skeleton variant="text" width="60%" height={16} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="40%" height={24} />
      </Box>
    ))}
  </Box>
);

// Skeleton para navegação/menu
export const NavigationSkeleton: React.FC<{
  items?: number;
}> = ({ items = 6 }) => (
  <Box sx={{ p: 1 }}>
    {Array.from({ length: items }).map((_, index) => (
      <Box
        key={`nav-item-${index}`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 1.5,
          mb: 1,
          borderRadius: 1,
        }}
      >
        <Skeleton variant="circular" width={24} height={24} />
        <Skeleton variant="text" width="70%" height={20} />
      </Box>
    ))}
  </Box>
);

// Skeleton para modais/dialogs
export const ModalSkeleton: React.FC<{
  showHeader?: boolean;
  showContent?: boolean;
  showActions?: boolean;
}> = ({ showHeader = true, showContent = true, showActions = true }) => (
  <Box
    sx={{
      p: 3,
      border: "1px solid #e0e0e0",
      borderRadius: 2,
      backgroundColor: "#fff",
      maxWidth: 500,
      width: "100%",
    }}
  >
    {showHeader && (
      <Box sx={{ mb: 2 }}>
        <Skeleton variant="text" width="60%" height={24} />
      </Box>
    )}
    {showContent && (
      <Box sx={{ mb: 2 }}>
        <Skeleton variant="text" width="100%" height={16} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="80%" height={16} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="90%" height={16} />
      </Box>
    )}
    {showActions && (
      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
        <Skeleton variant="rectangular" width={80} height={36} />
        <Skeleton variant="rectangular" width={80} height={36} />
      </Box>
    )}
  </Box>
);

const SkeletonLoader = {
  ConversationSkeleton,
  MessageSkeleton,
  TemplateSkeleton,
  ContactSkeleton,
  CampaignSkeleton,
  ListSkeleton,
  GridSkeleton,
  CardSkeleton,
  TableSkeleton,
  FormSkeleton,
  StatsSkeleton,
  NavigationSkeleton,
  ModalSkeleton,
};

export default SkeletonLoader;
